import React from "react";
import { motion } from "framer-motion";
import { ExternalLink, BookOpen, Sparkles } from "lucide-react";
import PageShell from "../components/PageShell";
import ImagePlaceholder from "../components/ImagePlaceholder";
import { journalUrl, projects } from "../data/projects";

// Use the birdhouses-in-Vienna street photograph as a soft visual anchor
const journalImage =
  "https://customer-assets.emergentagent.com/job_editorial-light-1/artifacts/xtbo689w_DSC01358.webp";

const Journal = () => {
  return (
    <PageShell testId="page-journal">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-4 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center"
        >
          <div className="lg:col-span-7">
            <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-[#A8D2FA] mb-4">
              Photography Journal
            </div>
            <h1 className="font-fraunces text-5xl md:text-7xl font-light text-slate-900 dark:text-slate-100 leading-[1.02] text-balance">
              Notes from behind the lens.
            </h1>
            <p className="mt-8 font-manrope text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-2xl">
              An exploratory photo blog I kept during my very first photography
              course — now completed. It documents the learning process,
              assignments, experiments, reflections and the early development
              of my photography.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={journalUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="journal-visit-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 px-8 py-4 font-manrope text-sm font-medium tracking-wide hover:bg-slate-800 dark:hover:bg-slate-100 hover:-translate-y-0.5 transition-all duration-300 shadow-[0_12px_32px_rgba(15,23,42,0.18)]"
              >
                Visit Photography Journal
                <ExternalLink size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <span className="font-manrope text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
                Opens the journal in a new tab
              </span>
            </div>

            <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <JournalStat icon={<BookOpen size={16} />} label="Format" value="Long-form" />
              <JournalStat icon={<Sparkles size={16} />} label="Course" value="First · Completed" />
              <JournalStat icon={<ExternalLink size={16} />} label="Hosted" value="Externally" />
            </div>
          </div>

          <div className="lg:col-span-5">
            <ImagePlaceholder
              src={journalImage}
              alt="Photograph by STXP"
              ratio="aspect-[4/5]"
              rounded="rounded-[36px]"
              label="JOURNAL · NOTES"
            />
          </div>
        </motion.div>

        {/* Second block */}
        <div className="mt-24 glass-strong rounded-[32px] p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-8">
              <h2 className="font-fraunces text-3xl md:text-4xl font-light text-slate-900 dark:text-slate-100 leading-tight">
                A separate space — kept intentionally distinct from the portfolio.
              </h2>
              <p className="mt-4 font-manrope text-slate-600 dark:text-slate-300 leading-relaxed max-w-2xl">
                The journal is where I write freely: assignments, contact
                sheets, mistakes I&apos;m learning from, and quiet reflections. It
                lives on its own site so it can grow at its own pace.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <a
                href={journalUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="journal-visit-btn-secondary"
                className="inline-flex items-center gap-2 rounded-full bg-[#E8F4FF] dark:bg-white/[0.08] text-slate-800 dark:text-slate-200 border border-[#C6E2FF]/50 dark:border-white/10 px-6 py-3 font-manrope text-sm font-medium hover:bg-[#C6E2FF] dark:hover:bg-white/[0.12] transition"
              >
                Open journal <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

const JournalStat = ({ icon, label, value }) => (
  <div className="glass rounded-2xl p-5 flex items-center gap-3">
    <div className="w-9 h-9 rounded-xl bg-[#E8F4FF] dark:bg-white/[0.08] border border-[#C6E2FF]/50 dark:border-white/10 grid place-items-center text-slate-700 dark:text-slate-200">
      {icon}
    </div>
    <div>
      <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-slate-400 dark:text-slate-500">
        {label}
      </div>
      <div className="font-fraunces text-base text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  </div>
);

export default Journal;
